import { EditOutlined } from "@mui/icons-material";
import { Box, Button, ButtonProps, ChipProps, FormControl, FormLabel, IconButton, OutlinedInput, Paper, Typography } from "@mui/material";
import React, { ReactNode } from "react";
import { fioOrName, getUser, hasKey, isAdmin } from "../../models/Identity";
import Proxy from "../../models/Proxy";
import { getTenant } from "../../models/Tenant";
import { Contact, Person } from "../../models/Types";
import { getTheme } from "../../theme";
import { isMobile } from "../../utils";
import MobileSwipeableDrawer from "../MobileSwipeableDrawer";
import { DataGridApiRef } from "../data/ApiRef";
import { AbstractTypedDataForm } from "../data/DataForm";
import { FormTextInput } from "../form/TextInput";
import { Contacts } from "./Contacts";
import { PersonUser } from "./PersonUser";
import './person.css';

export class PersonForm<T extends Person = Person, TProps = {}, TState = {}> extends
    AbstractTypedDataForm<T, TProps, TState & { editPart?: string, contacts: Contact[] }> {
    protected contactsApi?: DataGridApiRef<T>
    // protected responseEntityProp = 'entity';
    protected getTypeId() {
        return 'Person';
    }

    protected getAccessKey() {
        return this.getTypeId();
    }

    protected createEntity() {
        return new Person() as T;
    }

    clear(): void {
        this.setState({
            id: 0,
            contacts: [],
            entity: new Person()
        });
    }

    protected responseToState(response: any) {
        const state = super.responseToState.call(this, response);
        state && (state.contacts = state.entity.getContacts());
        return state;
    }

    save(e: React.FormEvent<HTMLFormElement>) {
        this.state.entity.setContacts(this.state.contacts);
        return super.save.call(this, e);
    }

    buildButtons(entity?: Person) {
        return [];
    }

    buildDeleteButton(text?: ReactNode, props?: ButtonProps) {
        return hasKey(this.getAccessKey()) ? super.buildDeleteButton(text || 'Удалить пользователя', props) : <></>;
    }

    protected buildPersonMain(entity?: T) {
        return <>
            <FormTextInput required={true} label="Имя (обращение)" name="Name" form={this} entity={entity} />
            <FormTextInput label="Фамилия" name="Surname" form={this} entity={entity} />
            <FormTextInput label="Отчество" name="Patronomic" form={this} entity={entity} />
        </>;
    }

    protected buildPersonItems(entity?: T) {
        return [
            this.buildPersonMain(entity),

            <Box sx={{ my: 1 }}>
                <Contacts contacts={this.state?.contacts} apiRef={x => this.contactsApi = x} />
            </Box>,

            <FormControl>
                <FormLabel>Примечание</FormLabel>
                <OutlinedInput name="description" multiline={true} defaultValue={entity?.Description} onChange={this.onChange} />
            </FormControl>
        ];
    }

    protected buildPersonUser() {
        return <PersonUser
            typeName={this.getTypeName()}
            contacts={this.state?.contacts}
            client={this.state?.entity}
            onCreate={userId => this.onChange({ target: { name: 'userId', value: userId } })}
            onAddClick={() => this.contactsApi?.add()} />;
    }

    protected getStatuses(): ChipProps[] {
        return [];
    }

    protected getFormStyle() {
        if (isMobile()) {
            return {
                ...super.getFormStyle(),
                display: 'block',
                overflow: 'auto',
            };
        } else {
            return {
                ...super.getFormStyle(),
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'flex-start',
                overflow: 'hidden',
                gap: 2
            }
        }
    }

    buildContactList(entity: T | undefined, contact: keyof T, contactName: keyof T, title?: string, type?: string) {
        if (!entity) {
            return null;
        }

        const cs = entity?.[contact] as string[];
        const l = cs?.length;

        if (!l) {
            return null;
        }

        return cs.map((x, i) => <Typography variant="body2">
            <Typography component="span" variant="body2" color="textSecondary">{(entity[contactName] as string[])[i] || (l > 1 ? null : title)}:</Typography> {
                (entity[contact] as string[])[i]
            }</Typography>);
    }

    protected buildItems(entity?: T) {
        const ism = isMobile();
        if (ism) {
            return <>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, mt: 2 }}>
                    <Typography variant="h1">{fioOrName()}</Typography>
                    <Typography variant="overline">{getUser()?.Name}</Typography>
                </Box>

                <Paper key={'main' + this.state?.editPart} sx={{ bgcolor: 'info.main', mt: 1.5 }}>
                    <Typography variant="h2" display="flex" alignItems="center" justifyContent="space-between">
                        Основная информация
                        <IconButton onClick={() => this.setState({ editPart: 'main' })}><EditOutlined color="primary" /></IconButton>
                    </Typography>
                    <Typography variant="body2"><Typography component="span" variant="body2" color="textSecondary">ФИО:</Typography> {fioOrName()}</Typography>
                    <Typography variant="body2"><Typography component="span" variant="body2" color="textSecondary">Адрес:</Typography> {getTenant()?.
                        Houses?.map(x => x.Street + (x.Number ? ', ' + x.Number : '')).join(', ')}
                    </Typography>
                </Paper>

                <Paper key={'contacts' + this.state?.editPart} sx={{ bgcolor: 'info.main', mt: 1.5 }}>
                    <Typography variant="h2" display="flex" alignItems="center" justifyContent="space-between">
                        Контакты
                        <IconButton onClick={() => this.setState({ editPart: 'contacts' })}><EditOutlined color="primary" /></IconButton>
                    </Typography>
                    {this.buildContactList(entity, 'Phones', 'PhoneNames', 'Телефон', 'tel')}
                    {this.buildContactList(entity, 'Emails', 'EmailNames', 'E-mail', 'mailto')}
                    {this.buildContactList(entity, 'Links', 'LinkNames', 'Ссылка', '')}
                </Paper>

                {this.isReadOnly() !== false ? null : <Paper sx={{ flex: 1 }} elevation={2}>{this.buildPersonItems(entity)}</Paper>}

                {this.isReadOnly() !== false ? null : <Box sx={styles.buttonsBox}>{this.buildSaveButton()}</Box>}
            </>;
        }

        return <>
            <Box sx={{ alignSelf: 'stretch', overflow: 'auto' }}>
                <Paper sx={{ minWidth: ism ? undefined : 500, flex: 'none' }}>
                    {this.buildPersonItems(entity)}

                    <Box sx={styles.buttonsBox}>
                        {entity?.Id ? this.buildDeleteButton() : null}
                        <div style={{ flex: 1 }} />
                        {this.buildSaveButton()}
                    </Box>
                </Paper>
            </Box>
            {entity?.Id ? <Paper>
                {this.buildPersonUser()}
            </Paper> : null}
        </>;
    }

    render() {
        if (!this.state?.entity) {
            return super.render();
        }

        return <>
            {super.render()}

            <MobileSwipeableDrawer
                open={this.state?.editPart === 'main'}
                onOpen={() => { }}
                onClose={() => this.setState({ editPart: undefined })}
                PaperProps={{
                    onSubmit: e => {
                        e.preventDefault();
                        e.stopPropagation();

                        this.withLoading(() => Proxy.post('PersonSaveMain', {
                            Name: this.state.entity.Name,
                            Surname: this.state.entity.Surname,
                            Patronomic: this.state.entity.Patronomic
                        }).then(x => {
                            if (x.success && x.result) {
                                this._changed = false;
                                if (this.onSave() !== false) {
                                    this.dirtyHandler && this.dirtyHandler(false);
                                    this.observer.fire('save', [x.result as number, this.state.entity]);
                                }

                                let person = getTenant()?.Person;
                                if (person) {
                                    person.Name = this.state.entity.Name;
                                    person.Surname = this.state.entity.Surname;
                                    person.Patronomic = this.state.entity.Patronomic;
                                }

                                this.setState({ editPart: undefined });
                            }
                        }), 1000)
                    }
                }}>
                {this.buildPersonMain(this.state.entity)}

                <Box display="flex" gap={1}>
                    <Button variant="outlined" sx={{ flex: 1 }} onClick={() => this.setState({ editPart: undefined })}>Отмена</Button>
                    <Button variant="contained" sx={{ flex: 1 }} type="submit">Сохранить</Button>
                </Box>
            </MobileSwipeableDrawer>

            <MobileSwipeableDrawer
                PaperProps={{
                    sx: {
                        "& .contacts": {
                            overflowY: 'auto',
                            "& > div": {
                                flexDirection: 'column',
                                gap: 1,
                                my: 1,
                                p: 2,
                                borderRadius: getTheme().spacing(2),
                                boxShadow: getTheme().shadows[1],
                                "& > div > .MuiFormLabel-root": {
                                    fontWeight: 500,
                                    fontSize: 10,
                                    display: 'block'
                                }
                            }
                        }
                    },
                    onSubmit: e => {
                        e.preventDefault();
                        e.stopPropagation();

                        this.state.entity.setContacts(this.state.contacts);
                        this.withLoading(() => Proxy.post('PersonSaveContacts', this.state.entity).then(x => {
                            if (x.success && x.result) {
                                this._changed = false;
                                if (this.onSave() !== false) {
                                    this.dirtyHandler && this.dirtyHandler(false);
                                    this.observer.fire('save', [x.result as number, this.state.entity]);
                                }

                                this.setState({ editPart: undefined });
                            }
                        }), 1000)
                    }
                }}
                open={this.state?.editPart === 'contacts'}
                onOpen={() => { }}
                onClose={() => this.setState({ editPart: undefined })}>
                <Contacts contacts={this.state?.contacts} apiRef={x => this.contactsApi = x} />

                <Box display="flex" gap={1}>
                    <Button variant="outlined" sx={{ flex: 1 }} onClick={() => this.setState({ editPart: undefined })}>Отмена</Button>
                    <Button variant="contained" sx={{ flex: 1 }} type="submit">Сохранить</Button>
                </Box>
            </MobileSwipeableDrawer>
        </>;
    }
}

const styles = {
    buttonsBox: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 1
    },
    statusChips: {
    }
}

Object.assign(styles.statusChips, styles.buttonsBox);